import type { BaseOptionType } from "antd/lib/select"

import type { StoredPage } from "../../../redux/shared/paged-list.ts"
import { type SaveResult } from "../../../redux/shared/types.ts"
import type { BaseFilters, BasePagination } from "../../../types/api.ts"
import type { AuditLogEntry } from "../../../types/audit-log.types.ts"
import { type FinancialAlertsPayload } from "./project-finance/project-finance.thunks.ts"
import { type ProjectChecklistItem } from "../types/project-checklists.types.tsx"
import type {
  FinancialTransactionItem,
  FinancialTransactionSnapshot,
  FinancialTransactionSummary
} from "../types/project-finance.types.tsx"
import {
  type InvoiceSnapshot,
  type ProjectInvoice
} from "../types/project-invoices.types.tsx"
import type { MilestoneDependency } from "../types/project-milestone-dependency.types.ts"
import type { ProjectMilestone } from "../types/project-phases.types.tsx"
import type { ProjectSubcontractor } from "../types/project-subcontractors.types.tsx"
import { type Project } from "../types/project.types.ts"
import { type ProjectCommissionsData } from "./project-commissions/project-commissions.types.ts"

interface ProjectFinancialTransactionFilters extends BaseFilters {
  item_type: null | number
  project_type: null | string
  entry_type: null | string
}

interface ProjectInvoiceFilters extends BaseFilters {
  type: null | number
  status: null | number
}

interface ProjectChecklistFilters extends BaseFilters {
  assigned_to?: string
  project_phase?: string | null
  project_type?: string
  overdue?: boolean
  status?: string
  priority?: string
}

type ProjectCommissionsFilters = BaseFilters

interface ProjectMilestoneFilters extends BaseFilters {
  project_type: string | null
}

interface ProjectSubcontractorFilters extends BaseFilters {
  ordering?: string | null
}

type ProjectMilestoneFilterOptionsKeys =
  | "checklist_item_status"
  | "status"
  | "project_managers"
  | "admins"
  | "subcontractors"
  | "category"

interface ProjectMilestoneFilterOptions
  extends Partial<Record<ProjectMilestoneFilterOptionsKeys, BaseOptionType[]>> {
  subcontractors?: Array<{
    label: string
    value: { id: string | null; contact_user: string | null }
  }>
}

type ProjectChecklistFilterOptionsKeys =
  | "project_phases"
  | "status"
  | "subcontractors"
  | "team_members"
  | "project_managers"
  | "admins"
  | "priority"
  | "milestones"

interface ProjectChecklistFilterOptions
  extends Record<ProjectChecklistFilterOptionsKeys, BaseOptionType[]> {
  project_type: Array<BaseOptionType & { milestones: BaseOptionType[] }>
}

const FETCHING_SNAPSHOT_ENUM = {
  FINANCIAL: "financial",
  INVOICE: "invoice",
  MILESTONES: "milestones"
} as const

type FetchingSnapshotEnum =
  (typeof FETCHING_SNAPSHOT_ENUM)[keyof typeof FETCHING_SNAPSHOT_ENUM]

interface ProjectState {
  project: Project | null

  // milestones
  milestones: StoredPage<ProjectMilestone>
  milestonesRequestId: string | null
  milestonesFilters: ProjectMilestoneFilters
  milestonesFilterOptions: ProjectMilestoneFilterOptions
  // Flat, project-scoped — fetched once alongside the milestone list and
  // consumed by both the table's dependency count and the Gantt view's
  // connector arrows; not paginated/filtered like the milestones themselves.
  milestoneDependencies: MilestoneDependency[]

  // financial transaction items
  financialTransactionItems: StoredPage<
    FinancialTransactionItem,
    FinancialTransactionSummary
  >
  financialTransactionItemsRequestId: string | null
  financialTransactionSnapshot: FinancialTransactionSnapshot | null
  financialAlerts: FinancialAlertsPayload | null
  financialTransactionFilters: ProjectFinancialTransactionFilters

  // invoices
  invoices: StoredPage<ProjectInvoice>
  invoicesRequestId: string | null
  invoiceSnapshot: InvoiceSnapshot | null
  invoicesFilters: ProjectInvoiceFilters

  // subcontractors
  subcontractors: StoredPage<ProjectSubcontractor>
  subcontractorsRequestId: string | null
  selectedSubcontractor: ProjectSubcontractor | null
  subcontractorsFilters: ProjectSubcontractorFilters

  // checklist
  checklistItems: StoredPage<ProjectChecklistItem>
  checklistItemsRequestId: string | null
  checklistItemsFilters: ProjectChecklistFilters
  checklistItemsFilterOptions: ProjectChecklistFilterOptions

  // commissions
  commissions: ProjectCommissionsData
  commissionsItemsFilters: ProjectCommissionsFilters

  // logs
  logs: AuditLogEntry[]

  showSnapshot: boolean
  // Set by the header's alert icon, consumed and cleared by
  // FinancialAlertsPanel — expands the sidebar's alert section and focuses
  // this rule instead of navigating away from the current page.
  financialAlertFocusRule: string | null
  fetching: boolean
  fetchingSection: boolean
  fetchingSnapshotMap: Record<FetchingSnapshotEnum, boolean>
  fetchingSubcontractors: boolean
  saveFetching: boolean
  saveResult: SaveResult
}

export type {
  ProjectState,
  ProjectFinancialTransactionFilters,
  ProjectInvoiceFilters,
  ProjectMilestoneFilters,
  ProjectChecklistFilters,
  ProjectCommissionsFilters,
  ProjectSubcontractorFilters,
  ProjectMilestoneFilterOptions,
  ProjectChecklistFilterOptionsKeys,
  ProjectChecklistFilterOptions,
  FetchingSnapshotEnum
}

export { FETCHING_SNAPSHOT_ENUM }
